import { createI18n } from 'vue-i18n'

import regex from '@dogfy/lib/regex'

import * as validators from '@vuelidate/validators'
import { parsePhoneNumber } from 'awesome-phonenumber'
import i18nConfig from '@dogfy/lang/i18n.config'
import config from '@dogfy/config'
import dayjs from 'dayjs'

const i18n = createI18n(i18nConfig)
const { t } = i18n.global || i18n

const { createI18nMessage, helpers } = validators

const withI18nMessage = createI18nMessage({ t })

export const setI18nValidatorLocale = (locale) => {
  i18n.global.locale.value = locale
}

export const minWeight = (min) => withI18nMessage(validators.minValue(min), { messagePath: () => 'common.error.minWeight', messageParams: () => ({ min }) })

export const maxWeight = (max) => withI18nMessage(validators.maxValue(max), { messagePath: () => 'common.error.maxWeight', messageParams: () => ({ max }) })

export const minLength = (min) => withI18nMessage(validators.minLength(min), { messagePath: () => 'common.error.minLength', messageParams: () => ({ min }) })

export const required = withI18nMessage(validators.required, { messagePath: () => 'common.error.requiredField' })
export const requiredIf = (condition) => withI18nMessage(validators.requiredIf(condition), { messagePath: () => 'common.error.requiredField' })

export const passwordsMatch = (condition) => withI18nMessage(validators.sameAs(condition), { messagePath: () => 'common.error.passwordsShouldMatch' })

const differentPasswordValidator = (currentPassword) => helpers.withParams({ type: 'differentPasswords', currentPassword }, (value) => currentPassword !== value)
export const differentPasswords = (currentPassword) => withI18nMessage(differentPasswordValidator(currentPassword), { messagePath: () => 'common.error.samePassword' })

const strongPasswordValidator = helpers.withParams({ type: 'strongPassword' }, (value) => regex.strongPassword.test(value))
export const strongPassword = withI18nMessage(strongPasswordValidator, { messagePath: () => 'common.error.passwordNotStrong' })

const weakPasswordValidator = helpers.withParams({ type: 'weakPassword' }, (value) => regex.weakPassword.test(value))
export const weakPassword = withI18nMessage(weakPasswordValidator, { messagePath: () => 'common.error.weakPasswordNotStrong' })

export const email = withI18nMessage(validators.email, { messagePath: () => 'common.error.wrongEmailFormat' })

const emailNotUsedValidator = (isNotUsed) => helpers.withParams({ type: 'emailNotUsed', isNotUsed }, (value) => isNotUsed)
export const emailNotUsed = (isNotUsed) => withI18nMessage(emailNotUsedValidator(isNotUsed), { messagePath: () => 'common.error.emailConflict' })

function isDomainWhitelisted (domainLevels) {
  const [sld, ...otherDomainLevels] = domainLevels

  const allowedTLDs = config.emailDomainWhitelist[sld]
  if (!allowedTLDs) {
    // No specific TLD restriction for this second-level domain.
    return true
  }

  return otherDomainLevels.every((level) => allowedTLDs.includes(level))
}

const emailDomainValidator = helpers.withParams({ type: 'emailDomain', email }, (value) => {
  const domainLevels = value.split('@')[1]?.split('.')
  return !!domainLevels && domainLevels.length >= 2 && isDomainWhitelisted(domainLevels)
})
export const emailDomain = withI18nMessage(emailDomainValidator, { messagePath: () => 'common.error.emailDomainTypo' })

const zipValidator = (country) => helpers.withParams({ type: 'zip', country }, (value) => !helpers.req(value) || regex.validZip[country].test(value))
export const zip = (country) => withI18nMessage(zipValidator(country), { messagePath: () => 'common.error.wrongFormat' })

const servedRegionValidator = (country) => helpers.withParams({ type: 'servedRegion', country }, (value) => {
  const unservedRegions = config.unservedRegions[country]
  return !helpers.req(value) || !regex.validZip[country].test(value) || !unservedRegions.find(unservedZip => RegExp(`^${unservedZip}`).test(value))
})
export const servedRegion = (country) => withI18nMessage(servedRegionValidator(country), { messagePath: () => 'common.error.unservedRegion' })

const phoneValidator = (country) => helpers.withParams({ type: 'phone', country }, (value) => !helpers.req(value) || (parsePhoneNumber(value, { regionCode: country }).valid))
export const phone = (country) => withI18nMessage(phoneValidator(country), { messagePath: () => 'common.error.wrongPhoneFormat' })

const mobilePhoneValidator = (country) => helpers.withParams({ type: 'mobilePhone', country }, (value) => !helpers.req(value) || parsePhoneNumber(value, { regionCode: country }).typeIsMobile)
export const mobilePhone = (country) => withI18nMessage(mobilePhoneValidator(country), { messagePath: () => 'common.error.phoneMustBeMobile' })

const phoneNotUsedValidator = (isNotUsed) => helpers.withParams({ type: 'phoneNotUsed', isNotUsed }, (value) => isNotUsed)
export const phoneNotUsed = (isNotUsed) => withI18nMessage(phoneNotUsedValidator(isNotUsed), { messagePath: () => 'common.error.phoneConflict' })

const alphaLatinValidator = helpers.withParams({ type: 'alphaLatin' }, (value) => !helpers.req(value) || regex.alphaLatin.test(value))
export const alphaLatin = withI18nMessage(alphaLatinValidator, { messagePath: () => 'common.error.onlyAlphabetCharacters' })

const alphanumericLatinValidator = helpers.withParams({ type: 'alphanumericLatin' }, (value) => !helpers.req(value) || regex.alphanumericLatin.test(value))
export const alphanumericLatin = withI18nMessage(alphanumericLatinValidator, { messagePath: () => 'common.error.onlyAlphanumericCharacters' })

const dateAvailableValidator = (isDateAvailable) => helpers.withParams({ type: 'dateAvailable', isDateAvailable }, (value) => isDateAvailable)
export const dateAvailable = (isDateAvailable) => withI18nMessage(dateAvailableValidator(isDateAvailable), { messagePath: () => 'common.error.unavailableDate' })

const minAgeValidator = (value, unit) => helpers.withParams({ type: 'minAge', value }, (date) => dayjs(date).isBefore(dayjs().subtract(value, unit)))
export const minAge = (value, unit = 'month') => withI18nMessage(minAgeValidator(value, unit), { messagePath: () => t('common.error.minimumAge', { value, unit: t(`common.label.${unit}`, value).toLowerCase() }, value) })
